import { useNavigate } from 'react-router-dom';
import { Trophy, Dumbbell, Clock, Home, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils';
import type { Workout } from '@/types';

interface WorkoutSummaryProps {
  workout: Workout;
}

function formatElapsed(startedAt: string, completedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const diffMin = Math.round((end - start) / 60000);
  if (diffMin < 60) return `${diffMin} min`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

export function WorkoutSummary({ workout }: WorkoutSummaryProps) {
  const navigate = useNavigate();

  const totalSets = workout.exercises.reduce(
    (sum, ex) => sum + ex.sets.length,
    0
  );

  return (
    <div className="max-w-md mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <Trophy className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Treeni valmis!
        </h2>
        <p className="text-gray-500">
          {formatDate(workout.startedAt, 'EEEE d.M.yyyy')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card p-3 text-center">
          <Clock className="h-5 w-5 mx-auto mb-1 text-primary-500" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {formatElapsed(workout.startedAt, workout.completedAt)}
          </p>
          <p className="text-xs text-gray-500">Kesto</p>
        </div>
        <div className="card p-3 text-center">
          <Dumbbell className="h-5 w-5 mx-auto mb-1 text-primary-500" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {workout.exercises.length}
          </p>
          <p className="text-xs text-gray-500">Liikettä</p>
        </div>
        <div className="card p-3 text-center">
          <ListChecks className="h-5 w-5 mx-auto mb-1 text-primary-500" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {totalSets}
          </p>
          <p className="text-xs text-gray-500">Sarjaa</p>
        </div>
      </div>

      {/* Per-exercise breakdown */}
      {workout.exercises.length > 0 && (
        <div className="card p-4 space-y-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Liikkeet
          </h3>
          {workout.exercises.map((we) => {
            const bestSet = we.sets.reduce(
              (best, s) =>
                s.weight !== null && (best === null || s.weight > best.weight!)
                  ? s
                  : best,
              null as typeof we.sets[0] | null
            );
            return (
              <div
                key={we.id}
                className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {we.exercise?.name || 'Tuntematon'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {we.sets.length} {we.sets.length === 1 ? 'sarja' : 'sarjaa'}
                  </p>
                </div>
                {bestSet && bestSet.weight !== null && (
                  <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    {bestSet.weight} kg x {bestSet.reps}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className={cn(
          'w-full flex items-center justify-center gap-2',
          'min-h-[52px] px-6 font-semibold rounded-xl',
          'bg-primary-600 text-white',
          'transition-all duration-200 touch-manipulation',
          'hover:bg-primary-700 active:scale-[0.98]',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
        )}
      >
        <Home className="h-5 w-5" />
        Takaisin etusivulle
      </button>
    </div>
  );
}
